import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings, StyledRemoteString } from '@acorns/web-utils'

import { ProductKey, TierKey } from 'generated/types'
import type { SubscriptionPricingContentQuery } from 'generated/types'

import {
  KeyBenefitsButton,
  PlanBenefitCheck,
  PlanBenefitCheckContainer,
  PlanBenefitItem,
  PlanBenefitText,
  PlanBenefits,
  PlanBenefitsList,
  PlanCardContainer,
  PlanDesc,
  PlanHeader,
  PlanHeaderText,
  PlanIncludes,
  PlanName,
  PlanPrice,
  PlanPriceRow,
  PlanRadio,
  StyledChevron,
} from './styled-components'

type TierOption =
  SubscriptionPricingContentQuery['tierGroup']['tierOptions'][number]

interface Props {
  tierKey: TierKey
  name: string
  description: string
  dailyPrice: string
  monthlyPrice: string
  selected: boolean
  expanded: boolean
  products: TierOption['tier']['products']
  matchProgram: TierOption['tier']['matchProgram']
  onClick: () => void
  onExpand: () => void
}

export const PlanCard = ({
  tierKey,
  name,
  description,
  dailyPrice,
  monthlyPrice,
  selected,
  expanded,
  products,
  matchProgram,
  onClick,
  onExpand,
}: Props) => {
  const { getRemoteString } = useRemoteStrings()
  const isGold = tierKey === TierKey.Platinum

  return (
    <PlanCardContainer
      tierKey={tierKey.toLowerCase()}
      selected={selected}
      gold={isGold}
      expanded={expanded}
      onClick={onClick}
      tabIndex={0}
      role="button"
      aria-pressed={selected}
    >
      <PlanHeader>
        <PlanHeaderText>
          <PlanName>{name}</PlanName>
          <PlanDesc selected={selected}>{description}</PlanDesc>
        </PlanHeaderText>
        <PlanRadio selected={selected} gold={isGold}>
          {selected && (
            <AcornsIcon
              icon={AcornsIcon.Icon.Checkmark}
              width={15}
              height={11}
            />
          )}
        </PlanRadio>
      </PlanHeader>
      <PlanPriceRow>
        <PlanPrice selected={selected} gold={isGold}>
          <strong>{dailyPrice}/day</strong>
          <span>({monthlyPrice} billed monthly)</span>
        </PlanPrice>
        <KeyBenefitsButton
          selected={selected}
          gold={isGold}
          expanded={expanded}
          onClick={(e) => {
            e.stopPropagation()
            onExpand()
          }}
        >
          {expanded
            ? getRemoteString({
                key: 'registration.tier_selection.hide_highlights',
              })
            : getRemoteString({
                key: 'registration.tier_selection.expand_highlights',
              })}
          <StyledChevron
            icon={AcornsIcon.Icon.UpChevron}
            selected={selected}
            gold={isGold}
            expanded={expanded}
          />
        </KeyBenefitsButton>
      </PlanPriceRow>
      {expanded && (
        <PlanBenefits>
          <PlanIncludes>Includes:</PlanIncludes>
          <PlanBenefitsList>
            {products
              .map((product) =>
                product.highlights?.map((highlight, i) => {
                  const stringVariables = (() => {
                    switch (product.key) {
                      case ProductKey.LaterMatch:
                        return [String(matchProgram?.matchPercentage)]
                      default:
                        return []
                    }
                  })()
                  return (
                    <PlanBenefitItem key={`${product.key}-highlight-${i}`}>
                      <PlanBenefitCheckContainer>
                        <PlanBenefitCheck
                          icon={AcornsIcon.Icon.Checkmark}
                          selected={selected}
                          gold={isGold}
                        />
                      </PlanBenefitCheckContainer>
                      <PlanBenefitText>
                        <StyledRemoteString
                          stringKey={highlight.title}
                          variables={stringVariables}
                        />
                      </PlanBenefitText>
                    </PlanBenefitItem>
                  )
                }),
              )
              .reduce<React.ReactNode[]>(
                (acc, items) => acc.concat(items ?? []),
                [],
              )}
          </PlanBenefitsList>
        </PlanBenefits>
      )}
    </PlanCardContainer>
  )
}
